import request from '@/utils/request.js';
import { FORUM_API_URL } from '@/config/index.js';
import { getForumUid } from '@/utils/forum/forumUid';

const FORUM_PREFIX = '/platform-box-community';
const USER_PREFIX = '/platform-box-user';
const REQUIRED_GROUP_ID_OPTIONS = { requiredFields: ['groupId'] };

const post = (url, data = {}, options = {}) => {
  const { requiredFields = [], ...requestOptions } = options;
  const missingRequiredField = requiredFields.find((field) => {
    const value = data[field];
    return value === undefined || value === null || String(value).trim() === '';
  });
  if (missingRequiredField) {
    return Promise.reject({
      code: 'FORUM_REQUEST_PARAM_REQUIRED',
      endpoint: url,
      field: missingRequiredField,
      message: `Missing required forum request parameter: ${missingRequiredField}`,
    });
  }
  console.log('post', url, data, requestOptions);
  return request({
    baseURL: FORUM_API_URL,
    url: `${FORUM_PREFIX}${url}`,
    data,
    method: 'post',
    isForumRequest: true,
    requireForumAuth: true,
    requireSign: true,
    ...requestOptions,
  });
}
// ============ PlatformCommunityController ============

// 论坛个人中心
export const getUserCenter = () => post('/platformCommunity/userCenter');

// 论坛信息列表（我加入的 + 热门）
export const getForumList = () => post('/platformCommunity/forumList');

// 论坛详情
export const getGroupDetail = (groupId) =>
  post('/platformCommunity/groupDetail', { groupId }, REQUIRED_GROUP_ID_OPTIONS);

// 完成新人引导
export const completeGuide = (groupId) =>
  post('/platformCommunity/completeGuide', { groupId }, REQUIRED_GROUP_ID_OPTIONS);

// 论坛导航列表
export const getNavigationList = (groupId) =>
  post('/platformCommunity/navigationList', { groupId }, REQUIRED_GROUP_ID_OPTIONS);

// 论坛标签列表
export const getTagList = (groupId) =>
  post('/platformCommunity/tagList', { groupId }, REQUIRED_GROUP_ID_OPTIONS);

// 集合页详情
export const getPostAssembleInfo = (assembleId) =>
  post('/platformCommunity/postAssembleInfo', { assembleId });

// 集合页标签帖子列表
export const getPostAssemblePostList = (params) =>
  post('/platformCommunity/postAssemblePostList', { ...params });

// 置顶帖子列表
export const getPostTopList = (groupId) =>
  post('/platformCommunity/postTopList', { groupId }, REQUIRED_GROUP_ID_OPTIONS);

// 帖子列表（论坛首页主列表）
export const getPostList = (params = {}) =>
  post('/platformCommunity/postList', { ...params }, REQUIRED_GROUP_ID_OPTIONS);

// 帖子详情
export const getPostDetail = (postId) => post('/platformCommunity/postDetail', { postId });

// 发布帖子
export const sendPosts = (params) => post('/platformCommunity/sendPosts', { ...params });

// 删除自己的帖子
export const deleteOwnPost = (postId) => post('/platformCommunity/deleteOwnPost', { postId });

// 帖子搜索
export const postSearchList = (params) => post('/platformCommunity/postSearchList', { ...params });

// 我的帖子列表
export const getMyPostsList = (params = {}) =>
  post('/platformCommunity/myPostsList', {
    ...params,
    groupId: params.groupId || 0,
  });

// 我的评论列表
export const getMyCommentList = (params) => post('/platformCommunity/myCommentList', { ...params });

// 我点赞的帖子列表
export const getPraisePostsList = (params = {}) => {
  const data = typeof params === 'object' ? params : { lastPostId: params };
  return post('/platformCommunity/praisePostsList', { ...data });
};

// 我收藏的帖子列表
export const getCollectPostsList = (params = {}) => {
  const data = typeof params === 'object' ? params : { lastPostId: params };
  return post('/platformCommunity/collectPostsList', { ...data });
};

// 评论
export const comment = (params) =>
  post('/platformCommunity/comment', {
    imgList: [],
    commentId: 0,
    parentCommentId: 0,
    ...params,
  });

// 评论列表
export const getCommentList = (params) => post('/platformCommunity/commentList', { ...params });

// 评论列表 V2：1-最新，2-最热；仅空列表表示分页结束
export const getCommentListV2 = (params) => post('/platformCommunity/commentListV2', { ...params });

// 我的等级页聚合数据
export const getMyLevelInfo = (groupId) => post('/platformCommunity/myLevelInfo', { groupId });

// 当前论坛礼包分页列表
export const getGroupGiftList = (params) => post('/platformCommunity/groupGift/list', { ...params });

// 领取论坛礼包
export const receiveGroupGift = (groupId, configId) =>
  post('/platformCommunity/groupGift/receive', { groupId, configId }, { unHint: true });

// 修改用户昵称 + 头像
export const modifyUserInfo = (params) => post('/platformCommunity/modifyUserInfo', { ...params });

// 论坛消息列表
export const getCommunityMsgList = (params) => {
  if (!getForumUid()) {
    return Promise.resolve({ data: { total: 0, list: [], records: [] } });
  }
  return post('/platformCommunity/communityMsgList', { ...params }, { forumAuthMode: 'silent' });
};

// 论坛未读消息数量
export const getCommunityUnreadMsgCount = () => {
  if (!getForumUid()) {
    return Promise.resolve({ data: 0 });
  }
  return post('/platformCommunity/communityUnreadMsgCount', {}, { forumAuthMode: 'silent' });
};

// 论坛未读消息总览（六类消息总数及分类明细）
export const getCommunityUnreadMsgOverview = () => {
  if (!getForumUid()) {
    return Promise.resolve({ data: { total: 0, items: [] } });
  }
  return post('/platformCommunity/communityUnreadMsgOverview', {}, { forumAuthMode: 'silent' });
};

// 论坛未读回复汇总（仅返回最新一类：帖子评论或评论回复）
export const getCommunityUnreadReplySummary = () => {
  if (!getForumUid()) {
    return Promise.resolve({ data: { type: null, unreadCount: 0 } });
  }
  return post('/platformCommunity/communityUnreadReplySummary', {}, { forumAuthMode: 'silent' });
};

// 论坛消息标记已读
export const readCommunityMsg = (msgId) => {
  if (!getForumUid() || msgId === undefined || msgId === null || msgId === '') {
    return Promise.resolve({ success: false, data: false });
  }
  return post('/platformCommunity/readCommunityMsg', { msgId }, { forumAuthMode: 'silent' });
};

// 论坛消息一键已读
export const readAllCommunityMsg = () => {
  if (!getForumUid()) {
    return Promise.resolve({ success: false, data: false });
  }
  return post('/platformCommunity/readAllCommunityMsg', {}, { forumAuthMode: 'silent' });
};

// 点赞 / 取消点赞帖子
export const praiseOrCancelPosts = (postId, status) =>
  post('/platformCommunity/praiseOrCancelPosts', { postId, status });

// 收藏 / 取消收藏帖子
export const collectOrCancelPosts = (postId, status) =>
  post('/platformCommunity/collectOrCancelPosts', { postId, status });

// 点赞 / 取消点赞评论
export const praiseOrCancelComments = (commentId, status) =>
  post('/platformCommunity/praiseOrCancelComments', { commentId, status });

// 订阅 / 取消订阅圈子
export const joinOrQuitGroup = (groupId, status) =>
  post('/platformCommunity/joinOrQuitGroup', { groupId, status });

// 文件上传：FormData 保持原样，不走论坛签名和 uid 注入
export const uploadForumFile = (data) =>
  request({
    baseURL: FORUM_API_URL,
    url: `${USER_PREFIX}/file/ossUpload`,
    data,
    method: 'post',
    isForumRequest: true,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
